// Faza 2 sledzenia pozycji: jedyny modul dotykajacy Web Audio API i mikrofonu.
// `chromagram.ts` i `chord-detect.ts` sa czysta matematyka i testuja sie bez
// przegladarki - ten modul spina je z `getUserMedia`/`AnalyserNode`.
//
// Faza 3: obok chromagramu (co 200ms, akordy) na TYM SAMYM strumieniu
// zbieramy surowy dzwiek do bufora kolowego (`pcm.ts`) i co 1 s wysylamy
// ostatnie 6 s jako WAV 16 kHz do dopasowania (`stt.ts`). Okna zachodza na
// siebie, wiec wers nie jest ciety w polowie, a pozycja odswieza sie co 1 s,
// nie co 6-7 s jak przy rozlacznych kawalkach z MediaRecordera.
// Dwie sciezki nie wiedza nic o sobie poza wspoldzieleniem `MediaStream` -
// chromagram jest szybki i offline, STT jest wolny i (poza samym
// dopasowaniem) wymaga sieci albo serwera w tailnecie.

import { computeChroma } from './chromagram.ts';
import { detectChord, type DetectedChord } from './chord-detect.ts';
import { alignAudio, type SttConfig } from './stt.ts';
import { BuforKolowy, CZESTOTLIWOSC_STT, glosnosc, kodujWav, zmienProbkowanie } from './pcm.ts';
import { potwierdzKandydata, wersDoPokazania } from './sledzenie.ts';
import { matchTranscriptToLine, type LyricsLine } from './text-matcher.ts';

/** Stan śledzenia tekstu, do pokazania grającemu. */
export type StanSluchania =
  | { rodzaj: 'slucham' }
  | { rodzaj: 'slysze'; tekst: string; trafiony: boolean }
  | { rodzaj: 'brak-serwera' };

export interface SluchaczOpcje {
  onChord: (chord: DetectedChord) => void;
  /** `postep` 0..1: jak daleko w pokazanym wersie jest śpiewający (0, gdy nieznane). */
  onLine?: (lineIndex: number, source: string, postep: number) => void;
  onStan?: (stan: StanSluchania) => void;
  onError: (error: Error) => void;
  sttConfig?: SttConfig;
  lyricsLines?: LyricsLine[];
  intervalMs?: number;
}

export type StopFn = () => void;

const DOMYSLNY_INTERWAL_MS = 200;
const FFT_SIZE = 8192;
/** Długość okna wysyłanego do rozpoznawania i co ile je wysyłamy. */
const OKNO_STT_S = 6;
const KROK_STT_MS = 1000;
/** Okno ciszy (albo samej gitary grającej cicho) nie idzie na serwer. */
const PROG_GLOSNOSCI = 0.004;

/**
 * Uruchamia sluchanie mikrofonu i cykliczna detekcje akordow. Zwraca funkcje
 * zatrzymujaca (zwalnia strumien, kontekst audio i interwal). Odmowa
 * uprawnien albo brak API laduje w `onError`, nigdy nie rzuca wyjatku poza
 * ta funkcje - wywolujacy (przycisk w `main.ts`) ma dostac spokojny sygnal,
 * nie niezlapany `reject`.
 */
export async function startSluchanie(opcje: SluchaczOpcje): Promise<StopFn> {
  const {
    onChord, onLine, onStan, onError, sttConfig, lyricsLines, intervalMs = DOMYSLNY_INTERWAL_MS,
  } = opcje;

  let strumien: MediaStream | null = null;
  let kontekst: AudioContext | null = null;
  let idInterwalu: ReturnType<typeof setInterval> | null = null;
  let sttAktywny = true;
  let idTimeraStt: ReturnType<typeof setInterval> | null = null;
  let procesor: ScriptProcessorNode | null = null;

  const posprzataj = (): void => {
    sttAktywny = false;
    if (idTimeraStt !== null) { clearInterval(idTimeraStt); idTimeraStt = null; }
    if (procesor) { procesor.onaudioprocess = null; procesor.disconnect(); procesor = null; }
    if (idInterwalu !== null) {
      clearInterval(idInterwalu);
      idInterwalu = null;
    }
    strumien?.getTracks().forEach((track) => track.stop());
    strumien = null;
    if (kontekst && kontekst.state !== 'closed') void kontekst.close();
    kontekst = null;
  };

  try {
    strumien = await navigator.mediaDevices.getUserMedia({ audio: true });
    kontekst = new AudioContext();
    // iOS potrafi oddać kontekst wstrzymany, gdy powstał po `await`
    // (poza bezpośrednim gestem): bez wznowienia nie płynie ani jedna próbka.
    if (kontekst.state === 'suspended') await kontekst.resume().catch(() => undefined);
    const zrodlo = kontekst.createMediaStreamSource(strumien);
    const analizator = kontekst.createAnalyser();
    analizator.fftSize = FFT_SIZE;
    zrodlo.connect(analizator);

    const dane = new Uint8Array(analizator.frequencyBinCount);
    const sampleRate = kontekst.sampleRate;

    idInterwalu = setInterval(() => {
      analizator.getByteFrequencyData(dane);
      const chroma = computeChroma(dane, sampleRate, FFT_SIZE);
      const akord = detectChord(chroma);
      if (akord !== null) onChord(akord);
    }, intervalMs);

    // Faza 3: rozpoznawanie tekstu startuje TYLKO, gdy jest dokąd wysłać
    // (choć jedna warstwa STT) i do czego dopasować (wersy tekstu).
    const maSttWarstwe = !!(sttConfig && (sttConfig.elevenLabsKey || sttConfig.whisperUrl));
    if (maSttWarstwe && lyricsLines && lyricsLines.length > 0 && onLine) {
      const lyricsTexts = lyricsLines.map((l) => l.text);
      const bufor = new BuforKolowy(Math.ceil(sampleRate * OKNO_STT_S));
      // ScriptProcessor, nie AudioWorklet: działa w każdym Safari bez
      // osobnego pliku modułu, a tu przepisuje tylko próbki do bufora.
      procesor = kontekst.createScriptProcessor(4096, 1, 1);
      procesor.onaudioprocess = (zd) => bufor.dopisz(zd.inputBuffer.getChannelData(0));
      const cisza = kontekst.createGain();
      cisza.gain.value = 0; // Safari odpala `onaudioprocess` tylko dla węzła podpiętego do wyjścia.
      zrodlo.connect(procesor);
      procesor.connect(cisza);
      cisza.connect(kontekst.destination);

      let ostatniaLinia = -1;
      let zajety = false;
      let bledySieci = 0;
      /** Kandydat dalekiego skoku z poprzedniego okna, czeka na potwierdzenie. */
      let ostatniKandydat: number | null = null;
      onStan?.({ rodzaj: 'slucham' });

      const wyslij = async (): Promise<void> => {
        if (!sttAktywny || zajety || bufor.dlugosc < sampleRate * 1.5) return;
        const surowe = bufor.ostatnie(Math.ceil(sampleRate * OKNO_STT_S));
        if (glosnosc(surowe) < PROG_GLOSNOSCI) return;
        const wav = kodujWav(zmienProbkowanie(surowe, sampleRate, CZESTOTLIWOSC_STT), CZESTOTLIWOSC_STT);
        zajety = true;
        const start = performance.now();
        try {
          const wynik = await alignAudio(new Blob([wav], { type: 'audio/wav' }), lyricsTexts, sttConfig!, ostatniaLinia);
          if (!sttAktywny) return;
          if (!wynik) {
            bledySieci += 1;
            if (bledySieci >= 2) onStan?.({ rodzaj: 'brak-serwera' });
            return;
          }
          bledySieci = 0;
          const podroz = (performance.now() - start) / 1000;
          console.debug('[stt]', wynik.source, wynik.transcript, 'linia:', wynik.lineIndex, 'postep:', wynik.lineProgress, 'temu:', wynik.secondsAgo, 'podroz:', podroz.toFixed(2));
          let linia = wynik.lineIndex;
          if (linia < 0 && wynik.transcript && wynik.source === 'elevenlabs') {
            const lokalny = matchTranscriptToLine(wynik.transcript, lyricsLines);
            if (lokalny !== null) linia = lyricsLines.findIndex((l) => l.lineIndex === lokalny);
          }
          let postepLinii = wynik.lineProgress ?? 0;
          if (linia < 0 && typeof wynik.candidate === 'number') {
            if (potwierdzKandydata(ostatniKandydat, wynik.candidate)) {
              linia = wynik.candidate;
              postepLinii = wynik.candidateProgress ?? 0;
              ostatniKandydat = null;
            } else {
              ostatniKandydat = wynik.candidate;
            }
          } else {
            ostatniKandydat = null;
          }
          onStan?.({ rodzaj: 'slysze', tekst: wynik.transcript, trafiony: linia >= 0 });
          if (linia < 0) return;
          // Do serwera odsyłamy to, co SAM wskazał (bez naszej korekty):
          // korekta jest przewidywaniem i nie może się nawarstwiać.
          ostatniaLinia = linia;
          const pokaz = wersDoPokazania({ ...wynik, lineIndex: linia, lineProgress: postepLinii }, podroz, lyricsLines.length);
          const idx = lyricsLines[pokaz]?.lineIndex;
          // Po korekcie na następny wers postęp w nim jest nieznany, więc 0.
          if (idx !== undefined) onLine(idx, wynik.source, pokaz === linia ? postepLinii : 0);
        } catch {
          bledySieci += 1;
          if (bledySieci >= 2) onStan?.({ rodzaj: 'brak-serwera' });
        } finally {
          zajety = false;
        }
      };
      idTimeraStt = setInterval(() => { void wyslij(); }, KROK_STT_MS);
    }
  } catch (e) {
    posprzataj();
    onError(e instanceof Error ? e : new Error('Nie udalo sie uruchomic mikrofonu.'));
  }

  return posprzataj;
}
